import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  Users, 
  CheckCircle2, 
  XCircle,
  Loader2,
  Building2,
  Clock
} from "lucide-react";
import { organizationsAPI } from "@/lib/api";

export default function InvitationPage({ user }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (token) {
      loadInvitation();
    } else {
      setError("Token de invitación no válido");
      setLoading(false);
    }
  }, [token]);

  const loadInvitation = async () => {
    try {
      const response = await organizationsAPI.getInvitationInfo(token);
      if (response.data.valid) {
        setInvitation(response.data);
      } else {
        setError(response.data.reason === "expired" 
          ? "Esta invitación ha expirado" 
          : "Invitación no válida");
      }
    } catch (error) {
      setError("Invitación no encontrada o expirada");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!user) {
      // Redirect to login with return URL
      toast.info("Inicia sesión para aceptar la invitación");
      navigate(`/login?redirect=/invitation?token=${token}`);
      return;
    }
    
    setAccepting(true);
    try {
      const response = await organizationsAPI.acceptInvitation(token);
      toast.success(response.data.message);
      navigate("/team");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aceptar invitación");
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#34C759]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <Card className="bg-[#111111] border-[#333] max-w-md w-full">
          <CardContent className="py-12 text-center">
            <XCircle className="w-16 h-16 mx-auto text-red-400 mb-4" />
            <h3 className="text-xl font-semibold text-[#F4F4F5] mb-2">
              Invitación no válida
            </h3>
            <p className="text-[#71717A] mb-6">{error}</p>
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="border-[#333]"
            >
              Ir al inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <Card className="bg-[#111111] border-[#333] max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-[#34C759]/10 rounded-full flex items-center justify-center mb-4">
            <Users className="w-8 h-8 text-[#34C759]" />
          </div>
          <CardTitle className="text-2xl text-[#F4F4F5]">
            Invitación de Equipo
          </CardTitle>
          <CardDescription>
            Has sido invitado a unirte a un equipo en AVX RF PRO
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Organization Info */}
          <div className="bg-[#0A0A0A] rounded-lg p-4 border border-[#222]">
            <div className="flex items-center gap-3 mb-3">
              <Building2 className="w-5 h-5 text-[#34C759]" />
              <span className="text-[#71717A]">Organización</span>
            </div>
            <p className="text-lg font-semibold text-[#F4F4F5]">
              {invitation?.organization_name}
            </p>
          </div>
          
          {/* Invitation Details */}
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-[#222]">
              <span className="text-[#71717A]">Invitado por</span>
              <span className="text-[#F4F4F5]">{invitation?.invited_by}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-[#222]">
              <span className="text-[#71717A]">Tu rol</span>
              <span className="text-[#34C759] font-medium capitalize">{invitation?.role}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-[#71717A]">Email</span>
              <span className="text-[#F4F4F5]">{invitation?.email}</span>
            </div>
          </div>
          
          {/* Warning if email doesn't match */}
          {user && user.email?.toLowerCase() !== invitation?.email?.toLowerCase() && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <p className="text-yellow-400 text-sm">
                <Clock className="w-4 h-4 inline mr-2" />
                Esta invitación es para <strong>{invitation?.email}</strong>.
                Estás conectado como <strong>{user.email}</strong>.
              </p>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              className="flex-1 border-[#333]"
              onClick={() => navigate("/")}
            >
              Cancelar
            </Button>
            <Button
              className="flex-1 bg-[#34C759] hover:bg-[#34C759]/90 text-black"
              onClick={handleAccept}
              disabled={accepting}
            >
              {accepting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Aceptando...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {user ? "Aceptar Invitación" : "Iniciar Sesión y Aceptar"}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
